import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Globe2, TrendingUp, Users, HeartHandshake, MapPin, Clock } from "lucide-react";
import Reveal from "../components/Reveal";
import CtaSection from "../components/CtaSection";

const perks = [
  { icon: Globe2, title: "Global by default", desc: "Work on infrastructure that moves money across markets — with a team that thinks beyond borders." },
  { icon: TrendingUp, title: "Own your impact", desc: "Small team, real responsibility. Your work ships and shapes the platform directly." },
  { icon: Users, title: "Build together", desc: "Close collaboration between engineering, product, design and compliance — no silos." },
  { icon: HeartHandshake, title: "Flexibility with trust", desc: "Remote-friendly, outcome-focused. We care about what you build, not where you sit." },
];

const roles = [
  { title: "Senior Backend Engineer, Payments", team: "Engineering", location: "Remote", type: "Full-time" },
  { title: "Full-Stack Engineer, Platform", team: "Engineering", location: "Remote", type: "Full-time" },
  { title: "Product Designer", team: "Design", location: "Remote", type: "Full-time" },
  { title: "Compliance & Risk Lead", team: "Compliance", location: "Remote", type: "Full-time" },
  { title: "Solutions Engineer", team: "Go-to-Market", location: "Remote", type: "Full-time" },
  { title: "Developer Relations Engineer", team: "Engineering", location: "Remote", type: "Contract" },
];

const Careers = () => (
  <div data-testid="careers-page">
    <section className="sw-grid-bg border-b border-[#E2E8F0] bg-white py-20 md:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="max-w-3xl">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#2E7CD6]">Careers</p>
          <h1 className="mt-4 font-heading text-4xl font-extrabold tracking-tight text-[#0F172A] sm:text-5xl lg:text-6xl">
            Build how the world moves money.
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#334155] md:text-lg">
            Swiftwire is a small, ambitious team building modern payment infrastructure. If you care about craft, clarity and global impact — we'd love to hear from you.
          </p>
        </Reveal>
      </div>
    </section>

    <section className="bg-white py-20 md:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="max-w-3xl">
          <h2 className="font-heading text-3xl font-bold tracking-tight text-[#0F172A] md:text-4xl">Why Swiftwire.</h2>
        </Reveal>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {perks.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.08}>
              <div className="h-full rounded-2xl border border-[#E2E8F0] bg-white p-7 shadow-sm" data-testid={`perk-${p.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#EFF5FD] text-[#1E3A6E]">
                  <p.icon size={20} strokeWidth={1.5} />
                </span>
                <h3 className="mt-5 font-heading text-lg font-bold text-[#0F172A]">{p.title}</h3>
                <p className="mt-2.5 text-sm leading-relaxed text-[#64748B]">{p.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="border-y border-[#E2E8F0] bg-[#F8FAFC] py-16 md:py-24" data-testid="life-at-swiftwire">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="max-w-3xl">
          <h2 className="font-heading text-3xl font-bold tracking-tight text-[#0F172A] md:text-4xl">Life at Swiftwire.</h2>
          <p className="mt-3 text-base text-[#334155]">A global team building global infrastructure — together.</p>
        </Reveal>
        <div className="mt-10 grid gap-5 sm:grid-cols-3">
          {[
            { img: "photo-1522071820081-009f0129c71c", alt: "Team members collaborating around a table with laptops" },
            { img: "photo-1531482615713-2afd69097998", alt: "Two colleagues working together at a computer" },
            { img: "photo-1521737604893-d14cc237f11d", alt: "A diverse team joining hands in celebration" },
          ].map((p, i) => (
            <Reveal key={p.img} delay={i * 0.08}>
              <div className="h-60 overflow-hidden rounded-2xl border border-[#E2E8F0] shadow-sm">
                <img
                  src={`https://images.unsplash.com/${p.img}?w=900&h=560&q=80&auto=format&fit=crop&crop=faces`}
                  alt={p.alt}
                  loading="lazy"
                  className="h-full w-full object-cover object-top transition-transform duration-500 hover:scale-[1.03]"
                />
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="bg-white py-20 md:py-24" data-testid="open-roles">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <Reveal className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
          <div>
            <h2 className="font-heading text-3xl font-bold tracking-tight text-[#0F172A] md:text-4xl">Open roles.</h2>
            <p className="mt-3 text-base text-[#334155]">We're hiring across engineering, design and operations.</p>
          </div>
          <span className="rounded-full bg-[#EFF5FD] px-4 py-1.5 text-sm font-semibold text-[#1E3A6E]">{roles.length} open roles</span>
        </Reveal>
        <div className="mt-10 space-y-4">
          {roles.map((r, i) => (
            <Reveal key={r.title} delay={Math.min(i * 0.05, 0.2)}>
              <Link
                to="/contact"
                data-testid={`role-${r.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="group flex flex-col gap-4 rounded-2xl border border-[#E2E8F0] bg-white p-6 shadow-sm transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-md sm:flex-row sm:items-center sm:justify-between sm:p-7"
              >
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-[#2E7CD6]">{r.team}</p>
                  <h3 className="mt-1.5 font-heading text-lg font-bold text-[#0F172A]">{r.title}</h3>
                  <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-[#64748B]">
                    <span className="flex items-center gap-1.5"><MapPin size={14} strokeWidth={1.5} /> {r.location}</span>
                    <span className="flex items-center gap-1.5"><Clock size={14} strokeWidth={1.5} /> {r.type}</span>
                  </div>
                </div>
                <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-[#E2E8F0] px-5 py-2.5 text-sm font-semibold text-[#1E3A6E] transition-colors group-hover:border-[#1E3A6E] group-hover:bg-[#1E3A6E] group-hover:text-white">
                  Apply
                  <ArrowUpRight size={15} strokeWidth={2} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-10">
          <div className="rounded-2xl border border-dashed border-[#CBD8EA] bg-white/60 p-6 text-center sm:p-8">
            <p className="text-sm text-[#334155]">
              Don't see your role? We're always happy to meet exceptional people.{" "}
              <Link to="/contact" data-testid="careers-open-application" className="font-semibold text-[#1E3A6E] hover:underline">
                Send an open application
              </Link>
            </p>
          </div>
        </Reveal>
      </div>
    </section>

    <CtaSection title="Want to build with us?" subtitle="Tell us who you are and what you'd love to work on." />
  </div>
);

export default Careers;
